import { ICON_COLOR } from '@zep-us/zus/tokens';
import { CheckBoldIcon } from '@zep-us/zus-icons';
import { useTranslation } from 'next-i18next';
import { overlay } from 'overlay-kit';
import { NUDGE_ENTER_CLASS_NAME, nudgeEnterDelay } from '@/shared/features/classroom/consts/classLinkNudge';
import { useCountUp } from '@/shared/features/classroom/hooks/useCountUp';
import { cn } from '@/utils/tailwind.util';
import type { QuizSavedNudgeFrom } from '../consts/events.quiz-classroom-link-nudge';
import { ClassLinkNudge, trackClassLinkNudgeView } from './ClassLinkNudge';

/** 피그마 결과 카드 그림자 (shadow/key-strong + shadow/ambient-strong). */
const RESULT_CARD_SHADOW = 'shadow-[0_15.7px_25px_0_rgba(28,27,31,0.12),0_0_15.7px_0_rgba(28,27,31,0.08)]';
const BACK_CARD_CLASS_NAME = 'absolute inset-x-0 mx-auto h-[197px] rounded-[17px] bg-default shadow-[0_3px_12.5px_0_rgba(41,31,89,0.06)]';

/**
 * 「결과가 쌓인다」를 움직임으로 보여 준다(약 1.5초):
 * 창이 열린 뒤 결과 카드 등장 → 뒤 카드 2장이 카드 뒤에서 위로 펼쳐짐 → 학생 →
 * 막대가 차며 0→90% (같은 곡선) → 다 차면 체크.
 */
const ENTER_CLASS_NAME = NUDGE_ENTER_CLASS_NAME;
const STACK_CLASS_NAME = 'motion-safe:animate-class-link-stack-in';
const CHECK_CLASS_NAME = 'motion-safe:animate-class-link-check-in';
const enterDelay = nudgeEnterDelay;
const TIMING = {
  /** 창 등장(ResponsiveDialog 200ms)과 겹치지 않게 그 뒤에 시작한다. */
  resultCard: 150,
  backCardNear: 350,
  backCardFar: 430,
  students: 450,
  studentGap: 70,
  progress: 550,
  /** tailwind `class-link-progress-fill` 길이와 같다. 숫자도 이 시간 동안 같은 곡선으로 오른다. */
  progressDuration: 900
} as const;
const SCORE_PERCENT = 90;

/** 결과 카드의 학생 아바타 3명(피그마 3283:28693~28695). 고정 꾸밈이다. */
const STUDENT_AVATAR_SRCS = [1, 2, 3].map(n => `/assets/classroom/class-link/quiz-saved-student-${n}.webp`);

/**
 * 피그마 QuizSavedModal 그림(3281:26501): 뒤에 겹친 카드 2장 + 앞 결과 카드(210×202).
 * 맵 자리만 이 퀴즈의 대표 이미지이고, 학생 아바타·정답률 줄은 고정 꾸밈이다(글자 없음).
 * 반 픽셀 위치에서 흐려지지 않도록 translate 대신 정수 좌표로 놓는다.
 */
function QuizSavedIllustration({
  mapImageUrl
}: {
  mapImageUrl: string;
}) {
  const score = useCountUp(SCORE_PERCENT, TIMING.progress, TIMING.progressDuration);
  return (
    // 피그마 그룹은 그림 판 가운데보다 4px 위에 있다.
    // 높이 224(피그마 223.6): 판 안에서 가운데 정렬해도 정수 위치에 놓인다.
    <div className="relative -top-3xs h-[224px] w-[210px] shrink-0" data-sentry-component="QuizSavedIllustration" data-sentry-source-file="QuizSavedLinkNudge.tsx">
      {/* 투명도는 바깥 래퍼에 둔다 — 등장 애니메이션이 opacity 를 1까지 올리기 때문이다. */}
      <div className="absolute inset-0 opacity-45">
        <div className={cn(BACK_CARD_CLASS_NAME, STACK_CLASS_NAME, 'top-0 w-[176px]')} style={enterDelay(TIMING.backCardFar)} />
      </div>
      <div className="absolute inset-0 opacity-75">
        <div className={cn(BACK_CARD_CLASS_NAME, STACK_CLASS_NAME, 'top-[9px] w-[194px]')} style={enterDelay(TIMING.backCardNear)} />
      </div>
      <div className={cn('absolute inset-x-0 top-[21px] flex h-[202px] flex-col items-start gap-[10px] overflow-hidden rounded-[17px] bg-default px-[10px] py-3', RESULT_CARD_SHADOW, ENTER_CLASS_NAME)} style={enterDelay(TIMING.resultCard)}>
        {/* 피그마 흰 테두리는 바깥쪽이라 흰 카드 위에선 안 보인다. 이미지가 188×123 을 꽉 채운다. */}
        <div className="h-[123px] w-[188px] shrink-0 overflow-hidden rounded-[14.6px] bg-[#D0EFA8]">
          {mapImageUrl && <img src={mapImageUrl} alt="" className="size-full object-cover" />}
        </div>
        <div className="flex w-full flex-col gap-[10px] pb-[3px]">
          <div className="flex items-center justify-between">
            <span className="flex items-center">
              {STUDENT_AVATAR_SRCS.map((src, index) => <img key={src} src={src} alt=""
              // 피그마 아바타(Avatar sm)를 4배(95px)로 내보낸 파일을 원으로 잘라 그린다.
              // 피그마 축척 값(23.5·겹침 3.9·테두리 2.4)을 정수로 맞췄다 — 소수점 위치면 이미지가 흐려진다.
              className={cn('size-6 rounded-full object-cover ring-2 ring-white', index > 0 && '-ml-1', ENTER_CLASS_NAME)} style={enterDelay(TIMING.students + index * TIMING.studentGap)} />)}
            </span>
            <span className="flex items-center gap-[3px] text-[15.7px] font-bold leading-[22px] text-positive">
              {/* 체크는 막대가 다 찬 뒤에 나타난다. 자리는 처음부터 차지해 숫자가 밀리지 않는다. */}
              <span className={cn('flex', CHECK_CLASS_NAME)}
              // 막대 곡선은 끝 1/3 이 거의 멈춰 있다. 다 찬 것처럼 보이는 지점에 맞춰 띄운다.
              style={enterDelay(TIMING.progress + TIMING.progressDuration * 2 / 3)}>
                {/* 피그마 아이콘 12.5px(그림 축척). ZUS 크기 토큰에 없는 값이라 width/height 로 준다. */}
                <CheckBoldIcon width={12.5} height={12.5} fill={ICON_COLOR.positive} data-sentry-element="CheckBoldIcon" data-sentry-source-file="QuizSavedLinkNudge.tsx" />
              </span>
              {/* 0%→90% 로 자릿수가 바뀌어도 폭이 같게 고정폭 숫자·최소 폭을 준다. */}
              {/* 숫자는 막대가 차기 시작할 때 함께 나타난다 — 빈 막대 옆에 0% 가 떠 있지 않게. */}
              <span className={cn('min-w-[3ch] text-right tabular-nums', ENTER_CLASS_NAME)} style={enterDelay(TIMING.progress)}>
                {score}%
              </span>
            </span>
          </div>
          <div className="h-[6.3px] w-full overflow-hidden rounded-[3px] bg-neutral">
            <div className="h-full w-4/5 origin-left rounded-[3px] bg-green-600 motion-safe:animate-class-link-progress-fill" style={enterDelay(TIMING.progress)} />
          </div>
        </div>
      </div>
    </div>
  );
}
interface QuizSavedNudgeProps {
  quizSetId: string;
  mapImageUrl: string;
  /** 새 퀴즈가 생긴 경로(새로 만들기·복제·복사). 계측용. */
  from: QuizSavedNudgeFrom;
}

/** ① 새 퀴즈 저장 직후 권유. 다른 것은 그림·문구뿐이고 동작은 `ClassLinkNudge` 가 맡는다. */
export function QuizSavedNudge({
  quizSetId,
  mapImageUrl,
  from,
  onClose
}: QuizSavedNudgeProps & {
  onClose: () => void;
}) {
  const {
    t
  } = useTranslation(['common']);
  return <ClassLinkNudge tracking={{
    place: 'quiz_saved_modal',
    quiz_set_id: quizSetId,
    from
  }} content={{
    id: quizSetId,
    type: 'QUIZSET'
  }} secondaryCloseReason="skip" illustration={<QuizSavedIllustration mapImageUrl={mapImageUrl} />} title={t('common:classroomLink.quizSavedNudge.title')} description={t('common:classroomLink.quizSavedNudge.description')} secondaryLabel={t('common:guestSave.skip')} onClose={onClose} data-sentry-element="ClassLinkNudge" data-sentry-component="QuizSavedNudge" data-sentry-source-file="QuizSavedLinkNudge.tsx" />;
}
export function showQuizSavedLinkNudge(props: QuizSavedNudgeProps) {
  trackClassLinkNudgeView({
    place: 'quiz_saved_modal',
    quiz_set_id: props.quizSetId,
    from: props.from
  });
  overlay.open(({
    unmount
  }) => <QuizSavedNudge {...props} onClose={unmount} />);
}